import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Ellipsis, Plus } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { setPreferGoogleLogin, clearPreferGoogleLogin } from '../../../utils/googleLoginPreference';
import './UserFormConnectedAccounts.css';

const API_BASE_URL = getApiBaseUrl();
const GOOGLE_AUTH_API = `${API_BASE_URL}/api/auth/google`;

const LINK_ERRORS = {
  GOOGLE_ALREADY_LINKED: 'Esta conta Google já está vinculada a outro usuário.',
  not_configured: 'OAuth Google não configurado no servidor.',
  tenant: 'Login Google desabilitado para este cliente.',
  callback: 'Não foi possível conectar a conta Google.',
};

/** Ícone oficial multicolorido do Google (G). */
export function GoogleGIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

/**
 * Linha "Contas conectadas" (formulário de usuário ou perfil).
 * @param {{ userId: number|string, onMessage?: (type: 'success'|'error', text: string) => void, variant?: 'row'|'stack' }} props
 */
export function UserFormConnectedAccounts({ userId, onMessage, variant = 'row' }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const isStack = variant === 'stack';

  const loadStatus = useCallback(async () => {
    if (!userId) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${GOOGLE_AUTH_API}/status?userId=${encodeURIComponent(userId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus(data.data || null);
      } else {
        setStatus(null);
      }
    } catch (_) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!pickerOpen && !menuOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setPickerOpen(false);
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [pickerOpen, menuOpen]);

  const handleConnectGoogle = async () => {
    if (busy || !userId) return;
    setPickerOpen(false);
    setBusy(true);
    try {
      const token = localStorage.getItem('authToken');
      const origin = encodeURIComponent(window.location.origin);
      const res = await fetch(
        `${GOOGLE_AUTH_API}/link-url?origin=${origin}&userId=${encodeURIComponent(userId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok || !data.success || !data.data?.url) {
        throw new Error(data.message || LINK_ERRORS.callback);
      }
      const popup = window.open(data.data.url, 'pilot-google-oauth', 'width=520,height=640');
      if (!popup) {
        throw new Error('Permita pop-ups para conectar a conta Google.');
      }
      await new Promise((resolve) => {
        const onMsg = (event) => {
          if (event.origin !== window.location.origin) return;
          if (!event.data || event.data.type !== 'pilot-google-oauth') return;
          window.removeEventListener('message', onMsg);
          window.clearInterval(closedTimer);
          if (event.data.ok) {
            try {
              const me = JSON.parse(localStorage.getItem('userData') || '{}');
              if (Number(me.id) === Number(userId)) setPreferGoogleLogin();
            } catch (_) {
              /* ignore */
            }
            onMessage?.('success', 'Conta Google conectada.');
            loadStatus();
          } else {
            const code = event.data.error || 'callback';
            onMessage?.('error', LINK_ERRORS[code] || LINK_ERRORS.callback);
          }
          resolve();
        };
        window.addEventListener('message', onMsg);
        const closedTimer = window.setInterval(() => {
          if (popup.closed) {
            window.clearInterval(closedTimer);
            window.removeEventListener('message', onMsg);
            resolve();
          }
        }, 500);
      });
    } catch (err) {
      onMessage?.('error', err.message || LINK_ERRORS.callback);
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    if (busy || !userId) return;
    setMenuOpen(false);
    if (!window.confirm('Desconectar a conta Google deste usuário?')) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${GOOGLE_AUTH_API}/link?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Falha ao desconectar');
      }
      onMessage?.('success', 'Conta Google desconectada.');
      try {
        const me = JSON.parse(localStorage.getItem('userData') || '{}');
        if (Number(me.id) === Number(userId)) clearPreferGoogleLogin();
      } catch (_) {
        /* ignore */
      }
      await loadStatus();
    } catch (err) {
      onMessage?.('error', err.message || 'Erro ao desconectar Google');
    } finally {
      setBusy(false);
    }
  };

  if (!userId) return null;

  const canConnect =
    status && status.enabled && status.configured && !status.linked && !loading;
  const linked = Boolean(status?.linked);

  return (
    <div
      className={[
        isStack ? 'user-form-connected-stack' : 'user-form-row user-form-row--connected',
      ].join(' ')}
      ref={wrapRef}
    >
      <div className={isStack ? 'user-form-connected-stack-label' : 'user-form-row-label'}>
        Contas conectadas
      </div>
      <div className="user-form-row-content user-form-connected-content">
        {loading ? (
          <span className="user-form-connected-muted">Carregando…</span>
        ) : (
          <>
            {linked ? (
              <div className="user-form-connected-item">
                <div className="user-form-connected-item-main">
                  <span className="user-form-connected-icon" aria-hidden>
                    <GoogleGIcon size={18} />
                  </span>
                  <span className="user-form-connected-label">
                    Google
                    {status.provider_email ? (
                      <>
                        <span className="user-form-connected-dot" aria-hidden>
                          ·
                        </span>
                        <span className="user-form-connected-email">{status.provider_email}</span>
                      </>
                    ) : null}
                  </span>
                </div>
                <div className="user-form-connected-menu-wrap" ref={menuRef}>
                  <button
                    type="button"
                    className="user-form-connected-menu-btn"
                    aria-label="Opções da conta Google"
                    aria-expanded={menuOpen}
                    disabled={busy}
                    onClick={() => {
                      setMenuOpen((o) => !o);
                      setPickerOpen(false);
                    }}
                  >
                    <Ellipsis size={16} strokeWidth={2} />
                  </button>
                  {menuOpen ? (
                    <div className="user-form-connected-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className="user-form-connected-menu-item user-form-connected-menu-item--danger"
                        disabled={busy}
                        onClick={handleUnlink}
                      >
                        Desconectar
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {canConnect ? (
              <div className="user-form-connected-actions">
                <div className="user-form-connected-picker-host">
                  <button
                    type="button"
                    className="user-form-link-btn user-form-connected-add"
                    disabled={busy}
                    onClick={() => {
                      setPickerOpen((o) => !o);
                      setMenuOpen(false);
                    }}
                  >
                    <Plus size={14} strokeWidth={2.25} aria-hidden />
                    Conectar conta
                  </button>
                  {pickerOpen ? (
                    <div className="user-form-connected-picker" role="listbox" aria-label="Provedores">
                      <button
                        type="button"
                        role="option"
                        className="user-form-connected-picker-item"
                        disabled={busy}
                        onClick={handleConnectGoogle}
                      >
                        <GoogleGIcon size={18} />
                        <span>Google</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {!linked && status && !status.enabled ? (
              <span className="user-form-connected-muted">
                Login Google desabilitado no Painel de controle
              </span>
            ) : null}

            {!linked && status?.enabled && !status?.configured ? (
              <span className="user-form-connected-muted">
                Credenciais Google não configuradas no servidor
              </span>
            ) : null}

            {!linked && !status && !loading ? (
              <span className="user-form-connected-muted">Indisponível</span>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default UserFormConnectedAccounts;
